import { useLoaderData, useParams, useNavigate, useRouteLoaderData } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useMemo } from 'react'
import Stack from '@mui/material/Stack'
import Divider from '@mui/material/Divider'
import Tooltip from '@mui/material/Tooltip'
import Button from '@mui/material/Button'
import IconButton from '@mui/material/IconButton'
import Typography from '@mui/material/Typography'
import EditIcon from '@mui/icons-material/Edit'
import ReviewsIcon from '@mui/icons-material/Reviews'
import { GridColDef, GridInitialState } from '@mui/x-data-grid-premium'

import StripedDataGrid from '../../../root/components/StripedDataGrid'
import { LoaderData } from '../../../../types'
import { useThemesDataOptions, useDomainsDataOptions } from '../../../../utils'
import { abstractReviewListEventsLoader, listAbstractReviewsLoader } from '../../Abstracts.loader'

const initialState: GridInitialState = {
  columns: {
    columnVisibilityModel: {
      id: false,
    },
  },
}

export default function AbstractsEvaluationList() {
  const { t } = useTranslation('abstracts')
  const { eventId } = useParams()
  const navigate = useNavigate()
  const abstractsList = useLoaderData() as LoaderData<ReturnType<typeof listAbstractReviewsLoader>>
  const abstracts = abstractsList?.results ?? []
  const events = useRouteLoaderData('review-abstracts-list') as LoaderData<
    ReturnType<typeof abstractReviewListEventsLoader>
  >

  const event = events?.find(event => event.id === Number(eventId))

  const themesDataOptions = useThemesDataOptions()
  const domainsDataOptions = useDomainsDataOptions()

  const columns: GridColDef<(typeof abstracts)[0]>[] = useMemo(
    () => [
      {
        field: 'actions',
        headerName: t('Actions'),
        type: 'actions',
        width: 80,
        filterable: false,
        disableExport: true,
        renderCell: ({ row }) => {
          return (
            <Stack direction="row">
              <Tooltip title={t('Évaluer')}>
                <IconButton
                  color="primary"
                  onClick={e => {
                    e.preventDefault()
                    e.stopPropagation()

                    navigate(`/abstracts/evaluation/${eventId}/rate/${row.id}`)
                  }}
                >
                  <ReviewsIcon />
                </IconButton>
              </Tooltip>
            </Stack>
          )
        },
      },
      {
        field: 'title',
        headerName: t('Titre du résumé'),
        width: 500,
      },
      {
        field: 'domain',
        headerName: t('Domaine'),
        width: 150,
        type: 'singleSelect',
        valueOptions: domainsDataOptions,
      },
      {
        field: 'theme',
        headerName: t('Thème'),
        width: 250,
        type: 'singleSelect',
        valueOptions: themesDataOptions,
      },
      {
        field: 'number_of_ratings',
        type: 'number',
        headerName: t('Nb éval'),
        width: 100,
      },
      {
        field: 'score',
        headerName: t('Note /10'),
        type: 'number',
        valueGetter: (_value, row) => row.review?.score,
        valueFormatter: (value?: number) => (value ? `${value}/10` : '-'),
        width: 100,
      },
    ],
    [domainsDataOptions, eventId, navigate, t, themesDataOptions]
  )

  return (
    <Stack flex="1 0" margin="1rem" sx={{ overflowY: 'auto' }}>
      {eventId === undefined || event === undefined ? (
        <Stack flex="1 0 auto" justifyContent="center" alignItems="center">
          <Typography variant="h2">{t('Veuillez sélectionner un événement valide')}</Typography>
        </Stack>
      ) : (
        <Stack flex="1 0 auto" gap="1rem">
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="h2">
              {event?.name} - {t('Résumés à évaluer')}
            </Typography>
            <Button
              startIcon={<EditIcon />}
              onClick={() => {
                navigate(`/profile`)
              }}
            >
              {t('Modifier mes domaines de compétence')}
            </Button>
          </Stack>
          <Divider sx={{ margin: 0 }} />
          <Typography>{t('Les évaluations seront ouvertes jusqu’au 27 février 2025')}</Typography>

          <Stack flexGrow="1" gap="1rem">
            <Typography variant="h4">{t('Chirurgie aortique et vasculaire​')}</Typography>
            <StripedDataGrid
              rows={abstracts}
              columns={columns}
              count={abstracts?.length}
              paginationMode="client"
              initialState={initialState}
              hideExport
              checkboxSelection={false}
            />
          </Stack>
        </Stack>
      )}
    </Stack>
  )
}
